import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewEncapsulation,
  inject,
  viewChild,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';
import { AppActions } from 'projects/semeris/src/store/app/app.actions';
import { LabelActions } from 'projects/semeris/src/store/label/label.actions';
import { BehaviorSubject, Observable, merge, timer } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { CommentCustomerFeedbackComponent } from '../../../comment/components/comment-customer-feedback/comment-customer-feedback.component';
import { CommentInputComponent } from '../../../comment/components/comment-input/comment-input.component';
import { CommentThreadComponent } from '../../../comment/components/comment-thread/comment-thread.component';
import { Comment } from '../../../comment/models/comment.model';
import { CommentService } from '../../../comment/services/comment.service';
import { FEEDBACK_TEMPLATE_MAP } from '../../../feedback/components/feedback-modal/feedback-modal.component';
import { WebSocketEntities } from '../../../shared/models/web-socket/web-socket.models';
import { WebSocketService } from '../../../shared/services/web-socket/web-socket.service';
import { ButtonDirective } from '../../../ui/button/button/button.directive';

@Component({
  selector: 'semeris-ai-help',
  imports: [
    CommonModule,
    CommentThreadComponent,
    CommentInputComponent,
    CommentCustomerFeedbackComponent,
    RouterLink,
    ButtonDirective,
  ],
  templateUrl: './ai-help.component.html',
  styleUrls: ['./ai-help.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiHelpComponent implements OnInit {
  private readonly commentService = inject(CommentService);
  private readonly webSocketService = inject(WebSocketService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  public comments$: Observable<Comment[]>;

  public thinking$ = new BehaviorSubject<boolean>(false);

  private loadTrigger$ = new BehaviorSubject<void>(undefined);

  private readonly messageFrom = new Date();
  public conversationId: string | null = null;
  private readonly channel = 'help-ai';

  public readonly commentScrollArea = viewChild<ElementRef<HTMLDivElement>>('commentScrollArea');

  public readonly commentInput = viewChild(CommentInputComponent);

  private readonly POLLING_INTERVAL = 15_000;

  public ngOnInit(): void {
    this.store.dispatch(LabelActions.DownloadLabels());
    const channels = [this.webSocketService.userChannel];

    merge(
      this.webSocketService.messages.pipe(
        filter((message) => {
          return (
            channels.includes(message.topic) &&
            message.payload.startsWith(`${WebSocketEntities.Comment}/all`)
          );
        }),
        map((message) => {
          const parsedMessage =
            /comment\/all\|(?<tabId>[a-z0-9-]+):(?<command>[A-Za-z0-9-,]+)/.exec(
              message.payload
            )?.groups;

          const [channel, conversationId, thinking] = parsedMessage?.command?.split(',');
          return { channel, thinking, conversationId };
        }),
        filter(({ conversationId }) => conversationId === this.conversationId)
      ),
      timer(this.POLLING_INTERVAL, this.POLLING_INTERVAL).pipe(map(() => ({ thinking: undefined })))
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ thinking }) => {
        if (thinking) {
          this.thinking$.next(thinking === 'start');
        } else {
          this.load();
        }
      });
  }

  public load() {
    this.loadTrigger$.next(undefined);
  }

  public focus() {
    this.commentInput()?.textInput?.nativeElement?.focus();
  }

  public onSend(question: string) {
    this.conversationId = uuid();

    this.comments$ = this.loadTrigger$.pipe(
      switchMap(() => {
        return this.commentService.getCommentsByConversation(this.conversationId, this.messageFrom);
      }),
      tap(() => {
        setTimeout(() => {
          this.commentScrollArea()?.nativeElement?.scroll({
            top: this.commentScrollArea().nativeElement.scrollHeight,
            behavior: 'smooth',
          });
        }, 150);
      })
    );
    this.commentService
      .addComment({
        entity: {
          channel: this.channel,
          conversationId: this.conversationId,
          markdown: question,
          html: question,
        },
      })
      .subscribe();
  }

  public onAnotherQuestion() {
    this.conversationId = null;
    this.comments$ = null;
  }

  public onContactSupport() {
    this.store.dispatch(AppActions.OpenFeedback({ ...FEEDBACK_TEMPLATE_MAP.get('feedback') }));
  }
}
